import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import { automationAPI, agentsAPI, teamsAPI, labelsAPI } from '../../services/api';
import { toast } from '../ui/use-toast';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Separator } from '../ui/separator';
import { Card, CardContent } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { ScrollArea } from '../ui/scroll-area';
import { Textarea } from '../ui/textarea';
import { cn } from '../../lib/utils';

interface AutomationRuleDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  ruleId?: number | null;
}

const EVENT_TYPES = [
  { value: 'conversation_created', label: 'Conversation Created' },
  { value: 'conversation_updated', label: 'Conversation Updated' },
  { value: 'message_created', label: 'Message Created' },
  { value: 'conversation_opened', label: 'Conversation Opened' },
];

const OPERATORS = [
  { value: 'equal_to', label: 'Equal to' },
  { value: 'not_equal_to', label: 'Not equal to' },
  { value: 'is_present', label: 'Is present' },
  { value: 'is_not_present', label: 'Is not present' },
  { value: 'contains', label: 'Contains' },
  { value: 'does_not_contain', label: 'Does not contain' },
  { value: 'starts_with', label: 'Starts with' },
  { value: 'ends_with', label: 'Ends with' },
  { value: 'greater_than', label: 'Greater than' },
  { value: 'less_than', label: 'Less than' },
];

const CONDITION_FIELDS = [
  { value: 'status', label: 'Status' },
  { value: 'priority', label: 'Priority' },
  { value: 'assignee_id', label: 'Assignee' },
  { value: 'team_id', label: 'Team' },
  { value: 'inbox_id', label: 'Inbox' },
  { value: 'message_content', label: 'Message Content' },
  { value: 'contact_email', label: 'Contact Email' },
  { value: 'contact_phone', label: 'Contact Phone' },
];

const ACTION_TYPES = [
  { value: 'assign_agent', label: 'Assign to Agent' },
  { value: 'assign_team', label: 'Assign to Team' },
  { value: 'remove_assignee', label: 'Remove Assignee' },
  { value: 'add_label', label: 'Add Label' },
  { value: 'remove_label', label: 'Remove Label' },
  { value: 'change_status', label: 'Change Status' },
  { value: 'change_priority', label: 'Change Priority' },
  { value: 'resolve_conversation', label: 'Resolve Conversation' },
  { value: 'send_message', label: 'Send Message' },
];

const AutomationRuleDialog: React.FC<AutomationRuleDialogProps> = ({
  open,
  onClose,
  onSave,
  ruleId,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    trigger_event: 'conversation_created',
    enabled: true,
  });

  const [conditions, setConditions] = useState<any[]>([
    { field_name: 'status', operator: 'equal_to', expected_value: '', group_operator: 'AND' },
  ]);

  const [actions, setActions] = useState<any[]>([
    { action_type: 'assign_agent', action_parameters: {}, execution_order: 0 },
  ]);

  const [agents, setAgents] = useState<any[]>([]);
  const [teams, setTeams] = useState<any[]>([]);
  const [labels, setLabels] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      void loadResources();
      if (ruleId) void loadRule();
      else resetForm();
    }
  }, [open, ruleId]);

  const loadResources = async () => {
    try {
      const [agentsData, teamsData, labelsData] = await Promise.all([
        agentsAPI.getAgents().catch(() => ({ agents: [] })),
        teamsAPI.getTeams().catch(() => ({ teams: [] })),
        labelsAPI.getLabels().catch(() => ({ labels: [] })),
      ]);
      setAgents(agentsData.agents || agentsData || []);
      setTeams(teamsData.teams || teamsData || []);
      setLabels(labelsData.labels || labelsData || []);
    } catch (err) {
      console.error('Failed to load resources:', err);
    }
  };

  const loadRule = async () => {
    try {
      setLoading(true);
      const rule = await automationAPI.getAutomationRule(ruleId!);
      setFormData({
        name: rule.name,
        description: rule.description || '',
        trigger_event: rule.trigger_event,
        enabled: rule.enabled,
      });
      if (rule.conditions && rule.conditions.length > 0) setConditions(rule.conditions);
      if (rule.actions && rule.actions.length > 0) setActions(rule.actions);
    } catch (err) {
      console.error('Failed to load rule:', err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      trigger_event: 'conversation_created',
      enabled: true,
    });
    setConditions([
      { field_name: 'status', operator: 'equal_to', expected_value: '', group_operator: 'AND' },
    ]);
    setActions([{ action_type: 'assign_agent', action_parameters: {}, execution_order: 0 }]);
  };

  const handleAddCondition = () => {
    setConditions([
      ...conditions,
      { field_name: 'status', operator: 'equal_to', expected_value: '', group_operator: 'AND' },
    ]);
  };

  const handleRemoveCondition = (index: number) => {
    setConditions(conditions.filter((_, i) => i !== index));
  };

  const handleConditionChange = (index: number, field: string, value: any) => {
    const next = [...conditions];
    next[index] = { ...next[index], [field]: value };
    setConditions(next);
  };

  const handleAddAction = () => {
    setActions([
      ...actions,
      { action_type: 'assign_agent', action_parameters: {}, execution_order: actions.length },
    ]);
  };

  const handleRemoveAction = (index: number) => {
    setActions(actions.filter((_, i) => i !== index));
  };

  const handleActionChange = (index: number, field: string, value: any) => {
    const next = [...actions];
    next[index] = { ...next[index], [field]: value };
    setActions(next);
  };

  const handleActionParameterChange = (index: number, paramKey: string, paramValue: any) => {
    const next = [...actions];
    next[index] = {
      ...next[index],
      action_parameters: {
        ...next[index].action_parameters,
        [paramKey]: paramValue,
      },
    };
    setActions(next);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const payload = {
        ...formData,
        conditions: conditions.map((c) => ({
          ...c,
          condition_group: c.condition_group || 'default',
        })),
        actions: actions.map((a, i) => ({
          ...a,
          execution_order: a.execution_order !== undefined ? a.execution_order : i,
        })),
      };

      if (ruleId) await automationAPI.updateAutomationRule(ruleId, payload);
      else await automationAPI.createAutomationRule(payload);

      onSave();
      onClose();
    } catch (err: any) {
      console.error('Failed to save rule:', err);
      toast({
        title: 'Could not save rule',
        description: err.response?.data?.detail || err.message || 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const renderActionParameters = (action: any, index: number) => {
    const params = action.action_parameters || {};

    switch (action.action_type) {
      case 'assign_agent':
        return (
          <div className="mt-4 space-y-3">
            <Label>Agent</Label>
            <Select
              value={params.agent_id != null && params.agent_id !== '' ? String(params.agent_id) : ''}
              onValueChange={(v) =>
                handleActionParameterChange(index, 'agent_id', v === '' ? '' : Number(v))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select agent" />
              </SelectTrigger>
              <SelectContent>
                {agents.map((agent) => (
                  <SelectItem key={agent.id} value={String(agent.id)}>
                    {agent.first_name} {agent.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 'assign_team':
        return (
          <div className="mt-4 space-y-3">
            <Label>Team</Label>
            <Select
              value={params.team_id != null && params.team_id !== '' ? String(params.team_id) : ''}
              onValueChange={(v) =>
                handleActionParameterChange(index, 'team_id', v === '' ? '' : Number(v))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select team" />
              </SelectTrigger>
              <SelectContent>
                {teams.map((team) => (
                  <SelectItem key={team.id} value={String(team.id)}>
                    {team.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 'add_label':
      case 'remove_label':
        return (
          <div className="mt-4 space-y-3">
            <Label>Label</Label>
            <Select
              value={params.label_id != null && params.label_id !== '' ? String(params.label_id) : ''}
              onValueChange={(v) =>
                handleActionParameterChange(index, 'label_id', v === '' ? '' : Number(v))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select label" />
              </SelectTrigger>
              <SelectContent>
                {labels.map((label) => (
                  <SelectItem key={label.id} value={String(label.id)}>
                    {label.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 'change_status':
        return (
          <div className="mt-4 space-y-3">
            <Label>Status</Label>
            <Select
              value={params.status || ''}
              onValueChange={(v) => handleActionParameterChange(index, 'status', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
              </SelectContent>
            </Select>
          </div>
        );

      case 'change_priority':
        return (
          <div className="mt-4 space-y-3">
            <Label>Priority</Label>
            <Select
              value={params.priority || ''}
              onValueChange={(v) => handleActionParameterChange(index, 'priority', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
              </SelectContent>
            </Select>
          </div>
        );

      case 'send_message':
        return (
          <div className="mt-4 space-y-3">
            <Label htmlFor={`action-msg-${index}`}>Message</Label>
            <Textarea
              id={`action-msg-${index}`}
              rows={3}
              value={params.message || ''}
              onChange={(e) => handleActionParameterChange(index, 'message', e.target.value)}
              placeholder="Message to send…"
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className={cn(
          'flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl',
        )}
      >
        <DialogHeader className="shrink-0 space-y-1.5 border-b border-border px-6 py-3.5 pr-14 text-left">
          <DialogTitle>{ruleId ? 'Edit Automation Rule' : 'Create Automation Rule'}</DialogTitle>
          <DialogDescription>
            When the trigger fires and conditions match, the actions below run in order.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[min(70vh,calc(90vh-8rem))] flex-1">
          <div className="space-y-6 px-6 py-5">
            {/* Rule info */}
            <div className="space-y-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Rule information
              </p>
              <div className="space-y-3">
                <Label htmlFor="rule-name">Rule name</Label>
                <Input
                  id="rule-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. VIP auto-assign"
                  required
                />
              </div>
              <div className="space-y-3">
                <Label htmlFor="rule-desc">Description</Label>
                <Textarea
                  id="rule-desc"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  placeholder="Optional notes for your team"
                />
              </div>
              <div className="space-y-3">
                <Label>Trigger event</Label>
                <Select
                  value={formData.trigger_event}
                  onValueChange={(v) => setFormData({ ...formData, trigger_event: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EVENT_TYPES.map((event) => (
                      <SelectItem key={event.value} value={event.value}>
                        {event.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-border/60 bg-muted/20 px-3 py-2.5">
                <div>
                  <p className="text-sm font-medium">Enable rule</p>
                  <p className="text-xs text-muted-foreground">Disabled rules never run</p>
                </div>
                <Switch
                  checked={formData.enabled}
                  onCheckedChange={(v) => setFormData({ ...formData, enabled: v })}
                />
              </div>
            </div>

            <Separator />

            {/* Conditions */}
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Conditions
                </p>
                <Button type="button" variant="outline" size="sm" className="h-8 gap-1" onClick={handleAddCondition}>
                  <Plus className="h-3.5 w-3.5" />
                  Add condition
                </Button>
              </div>
              {conditions.map((condition, index) => (
                <Card key={index} className="border-border/60 bg-muted/10 shadow-none">
                  <CardContent className="space-y-4 p-4 pt-4">
                    {index > 0 && (
                      <Select
                        value={condition.group_operator || 'AND'}
                        onValueChange={(v) => handleConditionChange(index, 'group_operator', v)}
                      >
                        <SelectTrigger className="h-8 w-[100px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="AND">AND</SelectItem>
                          <SelectItem value="OR">OR</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    <div className="grid gap-4 sm:grid-cols-12 sm:items-end">
                      <div className="space-y-2 sm:col-span-4">
                        <Label className="text-xs">Field</Label>
                        <Select
                          value={condition.field_name}
                          onValueChange={(v) => handleConditionChange(index, 'field_name', v)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CONDITION_FIELDS.map((field) => (
                              <SelectItem key={field.value} value={field.value}>
                                {field.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2 sm:col-span-4">
                        <Label className="text-xs">Operator</Label>
                        <Select
                          value={condition.operator}
                          onValueChange={(v) => handleConditionChange(index, 'operator', v)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {OPERATORS.map((op) => (
                              <SelectItem key={op.value} value={op.value}>
                                {op.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2 sm:col-span-3">
                        <Label className="text-xs">Value</Label>
                        <Input
                          value={condition.expected_value || ''}
                          onChange={(e) =>
                            handleConditionChange(index, 'expected_value', e.target.value)
                          }
                          placeholder="Compare to…"
                        />
                      </div>
                      <div className="flex sm:col-span-1 sm:justify-end">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9 text-muted-foreground hover:text-destructive"
                          onClick={() => handleRemoveCondition(index)}
                          disabled={conditions.length === 1}
                          aria-label="Remove condition"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Separator />

            {/* Actions */}
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Actions
                </p>
                <Button type="button" variant="outline" size="sm" className="h-8 gap-1" onClick={handleAddAction}>
                  <Plus className="h-3.5 w-3.5" />
                  Add action
                </Button>
              </div>
              {actions.map((action, index) => (
                <Card key={index} className="border-border/60 bg-muted/10 shadow-none">
                  <CardContent className="p-4">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                      <div className="min-w-0 flex-1 space-y-3">
                        <Label className="text-xs">Action type</Label>
                        <Select
                          value={action.action_type}
                          onValueChange={(v) => handleActionChange(index, 'action_type', v)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ACTION_TYPES.map((type) => (
                              <SelectItem key={type.value} value={type.value}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {renderActionParameters(action, index)}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive sm:mt-6"
                        onClick={() => handleRemoveAction(index)}
                        disabled={actions.length === 1}
                        aria-label="Remove action"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </ScrollArea>

        <DialogFooter className="shrink-0 gap-2.5 border-t border-border px-6 py-3.5">
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={
              loading || !formData.name || conditions.length === 0 || actions.length === 0
            }
            className="gap-2"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {loading ? 'Saving…' : ruleId ? 'Update rule' : 'Create rule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AutomationRuleDialog;
